import {
    TextField,
    DateField,
    useDataProvider,
    useNotify,
    Show,
    SimpleShowLayout,
    NumberField,
    Labeled,
    useShowContext,
    TopToolbar,
    Button,
} from "react-admin";
import { Box, Typography, Paper, Chip, Alert, Dialog, DialogContent, DialogActions, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import BackButton from "../../Components/BackButton";
import { apiUrl } from "../../Services/config";

// 状态选项定义（与列表页保持一致）
const STATUS_CHOICES = [
    { id: 1, name: "审核中" },
    { id: 2, name: "打款中" },
    { id: 3, name: "提现异常" },
    { id: 4, name: "已结清" }
];

// 状态颜色映射
const STATUS_COLORS = {
    1: "default",    // 审核中
    2: "primary",    // 打款中
    3: "error",      // 提现异常
    4: "success"     // 已结清
};

// 状态显示组件
const StatusField = ({ record }: { record?: any }) => {
    if (!record) return null;

    const status = STATUS_CHOICES.find(choice => choice.id === record.status);
    const color = STATUS_COLORS[record.status as keyof typeof STATUS_COLORS] || "default";

    return (
        <Chip
            label={status ? status.name : record.status}
            color={color}
            variant="outlined"
        />
    );
};

// 详情页面组件
export const withdrawalDetail = () => {
    const notify = useNotify();

    const onError = (error: any) => {
        notify(`详情请求失败`, { type: 'error' });
    };

    return (
        <Show queryOptions={{ onError }}>
            <WithdrawalDetailContent />
        </Show>
    );
};

// 获取文件URL
const getFileUrl = (fileName: string) => {
    if (!fileName) return null;
    return `${apiUrl}/files/file/${fileName}`;
};

// 凭证预览组件
const VoucherPreview = ({ fileName, open, onClose }: { fileName: string, open: boolean, onClose: () => void }) => {
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

    useEffect(() => {
        if (open && fileName) {
            const loadImage = async () => {
                try {
                    const token = localStorage.getItem('authToken');
                    const url = getFileUrl(fileName);

                    const response = await fetch(url, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error('获取图片失败');
                    }

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    setPreviewImageUrl(blobUrl);
                } catch (error) {
                    console.error("预览图片失败:", error);
                }
            };

            loadImage();
        }

        return () => {
            // 清理Blob URL
            if (previewImageUrl) {
                URL.revokeObjectURL(previewImageUrl);
            }
        };
    }, [open, fileName]);

    const handleClose = () => {
        setPreviewImageUrl(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>凭证预览</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {previewImageUrl ? (
                        <img
                            src={previewImageUrl}
                            alt="转账凭证"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <Typography>加载中...</Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>关闭</Button>
            </DialogActions>
        </Dialog>
    );
};

// 详情内容组件
const WithdrawalDetailContent = () => {
    const { record, isLoading, error, refetch } = useShowContext();
    const [hasError, setHasError] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                refetch();
            } catch (err) {
                console.error('加载数据失败:', err);
                setHasError(true);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        if (error) {
            setHasError(true);
            console.error('加载提现详情失败:', error);
        }
    }, [error]);

    // 构建返回设备详情页的路径
    const backPath = `/withdrawal/`;

    const handlePreviewVoucher = () => {
        if (record?.transferVoucher) {
            setPreviewOpen(true);
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
    };

    let getStast = () => {
        if (hasError) {
            return (
                <Box sx={{ maxWidth: 800, margin: '0 auto', p: 3 }}>
                    <Typography variant="h6" color="error">
                        无法加载提现详情
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        请检查网络连接或联系管理员
                    </Typography>
                </Box>
            );
        }

        if (isLoading) {
            return (
                <Box sx={{ maxWidth: 800, margin: '0 auto', p: 3 }}>
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography>加载中...</Typography>
                    </Paper>
                </Box>
            );
        }

        if (!record) {
            return (
                <Box sx={{ maxWidth: 800, margin: '0 auto', p: 3 }}>
                    <Alert severity="warning">
                        未找到提现记录
                    </Alert>
                </Box>
            );
        }

        return (
            <Box sx={{ maxWidth: 800, margin: '0 auto' }}>
                {/* 基础信息卡片 */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{
                        borderBottom: '1px solid #e0e0e0',
                        pb: 1,
                        mb: 2
                    }}>
                        提现基本信息
                    </Typography>

                    <SimpleShowLayout>
                        <TextField source="id" label="ID" />
                        <TextField source="salesCustomerName" label="销售客户" />
                        <TextField source="reconciliationMonth" label="对账月份" />

                        <Labeled label="提现金额">
                            <NumberField
                                source="amount"
                                options={{
                                    style: 'currency',
                                    currency: 'CNY'
                                }}
                            />
                        </Labeled>

                        <Labeled label="状态">
                            <StatusField record={record} />
                        </Labeled>

                        <TextField source="withdrawalNo" label="流水号" />
                    </SimpleShowLayout>
                </Paper>

                {/* 时间信息卡片 */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{
                        borderBottom: '1px solid #e0e0e0',
                        pb: 1,
                        mb: 2
                    }}>
                        时间信息
                    </Typography>

                    <SimpleShowLayout>
                        <DateField
                            source="createTime"
                            label="申请日期"
                            showTime
                            locales="zh-CN"
                        />
                        <DateField
                            source="transferDate"
                            label="完成日期"
                            showTime
                            locales="zh-CN"
                        />
                    </SimpleShowLayout>
                </Paper>

                {/* 凭证信息卡片 */}
                {record.transferVoucher && (
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{
                            borderBottom: '1px solid #e0e0e0',
                            pb: 1,
                            mb: 2
                        }}>
                            转账凭证
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                label="预览凭证"
                                onClick={handlePreviewVoucher}
                                variant="outlined"
                            />
                            <Typography variant="body2" color="text.secondary">
                                {record.transferVoucher}
                            </Typography>
                        </Box>
                    </Paper>
                )}

                {/* 备注信息卡片 */}
                {record.remark && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{
                            borderBottom: '1px solid #e0e0e0',
                            pb: 1,
                            mb: 2
                        }}>
                            备注信息
                        </Typography>

                        <SimpleShowLayout>
                            <TextField
                                source="remark"
                                label="备注"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            />
                        </SimpleShowLayout>
                    </Paper>
                )}
            </Box>
        );
    }

    return (
        <>
            <TopToolbar sx={{ justifyContent: 'start' }}>
                <BackButton to={backPath} />
            </TopToolbar>
            {getStast()}

            {/* 凭证预览对话框 */}
            {record?.transferVoucher && (
                <VoucherPreview
                    fileName={record.transferVoucher}
                    open={previewOpen}
                    onClose={handleClosePreview}
                />
            )}
        </>
    );
};

// 为了保持与App.tsx中的导入一致，导出默认组件
export default withdrawalDetail;